import { CirclePlusIcon, PhoneIcon, XIcon } from 'lucide-react'
import React, { useEffect } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import LoadingSpinnerCentered from 'components/reusable/LoadingSpinnerCentered'
import { Autocomplete, IconButton, TextInput, TextLink } from 'slab'
import { getCSRFToken, supportLinks } from 'utils'

import { useProviderInNetworkVerificationContext } from './ProviderInNetworkVerificationContext/ProviderInNetworkVerificationContext'

type Provider = {
  id?: string;
  title?: string | null;
  first_name?: string;
  last_name?: string;
  phone?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  specialty?: string;
  npi: number;
}

function getProviderName (provider: Provider) {
  return [
    provider.title,
    provider.first_name,
    provider.last_name,
  ].filter(Boolean).join(' ')
}

function ProviderSearchV2 (props: {
  zip: string;
  onSelect?: (providers: Provider[]) => void;
  providers?: Provider[] | null;
  hideDescription?: boolean;
}) {
  const { verifyingInNetworkStatusProviderNpis, verifyingDescription, inNetworkVerificationProgressStatus } = useProviderInNetworkVerificationContext()
  const [search, setSearch] = React.useState<string>('')
  const [searchResults, setSearchResults] = React.useState<Provider[]>([])
  const [selectedProviders, setSelectedProviders] = React.useState<Provider[]>(
    props.providers || []
  )
  const { verifyingProviders, otherProviders } = React.useMemo(() => {
    return {
      verifyingProviders: selectedProviders.filter(p => verifyingInNetworkStatusProviderNpis.includes(p.npi)),
      otherProviders: selectedProviders.filter(p => !verifyingInNetworkStatusProviderNpis.includes(p.npi)),
    }
  }, [selectedProviders, verifyingInNetworkStatusProviderNpis])
  const [debouncedValue] = useDebounceValue<string>(search, 400)
  const [isSearching, setIsSearching] = React.useState(false)

  useEffect(() => {
    const controller = new AbortController()

    // TODO: this is copy and paste from ProviderSearch.tsx, should be using react query instead
    const searchProviders = async () => {
      try {
        const response = await fetch(
          `/api/providers/search?searchKey=${debouncedValue}&zip=${props.zip}`, // eslint-disable-line thatch/no-bare-fetch
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          }
        )

        const data = await response.json()
        setSearchResults(data.providers)
      } catch (e) {
        console.error('There has been an error while retrieving providers', e)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    if (debouncedValue === '') {
      setSearchResults([])
    } else {
      searchProviders()
    }

    return () => {
      controller.abort()
    }
  }, [debouncedValue, props.zip])

  useEffect(() => {
    const controller = new AbortController()

    // Fetch the selected providers from the database
    // TODO: this is copy and paste from ProviderSearch.tsx, should be using react query instead
    const fetchSelectedProviders = async () => {
      try {
        const response = await fetch('/member_onboarding/providers/all', { // eslint-disable-line thatch/no-bare-fetch
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          signal: controller.signal,
        })

        const data = await response.json()
        if (data.providers) {
          setSelectedProviders(data.providers)
        }
      } catch (e) {
        console.error('There has been an error while retrieving providers for the member', e)
        setSelectedProviders([])
      }
    }

    fetchSelectedProviders()

    return () => {
      controller.abort()
    }
  }, [])

  // If the provider list has changed upstream and is different from selected providers,
  // then updated the list of selected providers
  useEffect(() => {
    if (props.providers?.length !== selectedProviders.length) {
      setSelectedProviders(props.providers || [])
    }
  }, [props.providers])

  const handleSelectProvider = (provider: Provider) => {
    // Check if the item is already selected
    const alreadySelected = selectedProviders.some(
      (selectedProvider) => selectedProvider.npi === provider.npi
    )

    // If this doctor has not yet been selected then add it to the selectedItems state
    // If was already selected at one point then don't do anything silently ignore
    if (!alreadySelected) {
      const updatedArray = [...selectedProviders, provider]
      setSelectedProviders(updatedArray)

      // TODO: this is copy and paste from ProviderSearch.tsx, should be using react query instead
      fetch('/member_onboarding/providers/', { // eslint-disable-line thatch/no-bare-fetch
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken() || '',
        },
        method: 'POST',
        body: JSON.stringify({ provider }),
      }).then(() => {
        props.onSelect?.(updatedArray)
      })
    }

    setSearchResults([])
  }

  const handleRemoveSelectedProvider = (providerNpi: number) => {
    const updatedArray = selectedProviders.filter(
      (obj) => obj.npi !== providerNpi
    )
    setSelectedProviders(updatedArray)

    // TODO: this is copy and paste from ProviderSearch.tsx, should be using react query instead
    fetch('/member_onboarding/providers/', { // eslint-disable-line thatch/no-bare-fetch
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': getCSRFToken() || '',
      },
      method: 'DELETE',
      body: JSON.stringify({ providerNpi }),
    }).then(() => {
      props.onSelect?.(updatedArray)
    })
  }

  function renderEmptyState () {
    let message

    if (!search) {
      message = 'Providers will appear as you type'
    } else if (isSearching) {
      message = (
        <span className='flex items-center gap-2'>
          <LoadingSpinnerCentered height='12px' width='12px' />
          Loading providers...
        </span>
      )
    } else if (searchResults.length === 0) {
      message = 'No providers found'
    }

    return <div className='px-4 py-3 body-sm text-gray-500'>{message}</div>
  }

  function renderSearchResults () {
    return (
      <>
        {searchResults.map((provider) => (
          <Autocomplete.Option
            key={provider.npi}
            onSelect={() => handleSelectProvider(provider)}
            value=''
          >
            <div className='flex w-full items-center justify-between'>
              <div>
                <span className='body-bold'>
                  {provider.title}{' '}
                  {provider.first_name + ' ' + provider.last_name}{' '}
                </span>
                <span className='body-sm'>
                  {provider.street_address}, {provider.city}, {provider.state}{' '}
                  <span className='text-gray-500'>{provider.specialty}</span>{' '}
                </span>
              </div>
              <div className='shrink-0 ml-4'>
                <CirclePlusIcon className='text-gray-500' size={16} />
              </div>
            </div>
          </Autocomplete.Option>
        ))}
      </>
    )
  }

  return (
    <div>
      {
        !props.hideDescription && (
          <>
            <h3
              className='heading-sm text-gray-800 mb-2'
              id='search'
            >
              Find your providers
            </h3>
            <p className='body-sm text-gray-500 mb-6'>
              Unfortunately, we can't guarantee the accuracy of the data provided as
              it changes often. We suggest{' '}
              <TextLink href={supportLinks.providerInNetwork} target='_blank'>
                double-checking with your providers and insurance company
              </TextLink>
              {' '}before enrolling.
            </p>
          </>
        )
      }
      <Autocomplete>
        <Autocomplete.Input asChild>
          <TextInput
            label='Search for providers'
            name='search'
            placeholder='Provider name'
            value={search}
            onChange={(event) => {
              const value = event.target.value
              setSearch(value)
              setIsSearching(!!value)
            }}
          />
        </Autocomplete.Input>
        <Autocomplete.List withPortal={false} zIndex={1}>
          {searchResults.length > 0
            ? renderSearchResults()
            : renderEmptyState()}
        </Autocomplete.List>
      </Autocomplete>
      {otherProviders.length > 0
        ? (
          <div className='mt-6'>
            <h4 className='body-sm-bold text-gray-800 mb-2'>Your providers</h4>
            <ul className='flex flex-col space-y-2'>
              {otherProviders.map((selectedProvider) => (
                <li
                  key={selectedProvider.npi}
                  className='flex items-center justify-between rounded-lg border border-gray-200 gap-2 p-2'
                >
                  <div className='w-full flex flex-col gap-y-1'>
                    <div>
                      <span className='body-sm-bold text-gray-800'>
                        {getProviderName(selectedProvider)}
                      </span>{' '}
                      <span className='body-sm text-gray-500'>
                        {selectedProvider.specialty}
                      </span>
                    </div>
                    <div className='caption text-gray-800'>
                      {selectedProvider.street_address} {selectedProvider.city},{' '}
                      {selectedProvider.state} {selectedProvider.zip_code}{' '}
                    </div>
                  </div>
                  <IconButton
                    onClick={() => {
                      handleRemoveSelectedProvider(selectedProvider.npi)
                    }}
                    icon={XIcon}
                    label={`Remove ${getProviderName(selectedProvider)}`}
                  />
                </li>
              ))}
            </ul>
          </div>
          )
        : (
          <div />
          )}
      {
        verifyingProviders.length > 0 && (
          <div className='mt-4 p-4 border border-thatch-600 bg-gradient-to-t from-thatch-100 to-thatch-200 text-gray-800 rounded-lg'>
            <div className='body-sm-bold flex gap-2 items-center'>
              <PhoneIcon size={14} />
              {inNetworkVerificationProgressStatus === 'done' ? 'Provider calls completed' : 'Provider calls in progress'}
            </div>
            <div className='body-sm my-3'>
              {verifyingDescription}
            </div>
            {
              verifyingProviders.map((provider) => (
                <div key={provider.npi} className='border rounded-lg p-3 border border-gray-200 bg-white body-sm mb-2'>
                  {getProviderName(provider)}
                </div>
              ))
            }
          </div>
        )
      }
    </div>
  )
}

export default ProviderSearchV2
